import { useCallback, useRef } from 'react';
import { mutate } from 'swr';

import { toast } from '@commenda/kakashi';
import type { AgentAttachment } from '@commenda/types';

import {
  commendaAiConversationsKey,
  commendaAiPendingKey,
  createCommendaAiConversation,
} from '@commenda/api/commenda-ai/api';
import type { IDocumentReaderUploadReference } from '@commenda/api/commenda-ai/document-reader/types';
import { DataRoomSendHelper } from './data-room/DataRoomSendHelper';
import { useCommendaAiWorkspace } from './useCommendaAiWorkspace';

interface UseCreateCommendaAiConversationOptions {
  /** Where the caller takes the user once the conversation exists: the full page navigates to it,
   * the panel makes it the active one. */
  onCreated: (conversationId: string) => void | Promise<void>;
  /** The agent the conversation runs as, for a surface that lets one be chosen. Omitted everywhere
   * else, which keeps the default chat profile. */
  profile?: string;
}

/** Create-on-send: stash the first message, create the conversation, then hand the id to the caller
 * so `CommendaAiConversation` flushes the stash on mount. Creating only on the first send is what stops
 * an empty conversation being spawned every time someone opens a blank chat and changes their mind.
 *
 * Rejects on failure after showing a toast, so a composer that keeps its draft on a rejected submit
 * leaves the text in place for a retry. */
export const useCreateCommendaAiConversation = ({
  onCreated,
  profile,
}: UseCreateCommendaAiConversationOptions) => {
  const {
    companyId,
    scope,
    isLoading: isConfigLoading,
  } = useCommendaAiWorkspace();
  const isCreatingRef = useRef(false);

  const createConversation = useCallback(
    async (
      text: string,
      attachments: AgentAttachment[] = [],
      dataRoom?: IDocumentReaderUploadReference
    ) => {
      if (isCreatingRef.current || isConfigLoading) return;
      isCreatingRef.current = true;

      try {
        const created = await createCommendaAiConversation(
          companyId,
          scope || profile
            ? { ...(scope ? { scope } : {}), ...(profile ? { profile } : {}) }
            : undefined
        );
        if (!created.success || !created.data) throw new Error(created.error);

        const conversation = created.data;
        // The room uploaded with no conversation is linked to this one by the first send.
        window.sessionStorage.setItem(
          commendaAiPendingKey(conversation.id),
          DataRoomSendHelper.toPendingMessage(
            text,
            attachments,
            dataRoom ?? null
          )
        );
        await mutate(commendaAiConversationsKey(companyId, true, scope));
        await onCreated(conversation.id);
      } catch (error) {
        toast.error('Could not start the chat. Please try again.');
        throw error;
      } finally {
        isCreatingRef.current = false;
      }
    },
    [companyId, scope, profile, isConfigLoading, onCreated]
  );

  return { createConversation };
};
